import { forEachElement, getElementById, moveAllChildren, watchEvent } from "../util/Utils";
import { filter, filterFooter, filterSheetButton, nextBestActionButtons } from "./Selectors";
import { fireFilterSubmit, hasChanges, isForceSubmit } from "../filter/FilterFormActions";
import { setActiveConfiguration, toggleFilterAccordion } from "../lib/filter";
import { resetFacetLocation, setFacetLocation } from "../lib/facet";
import { type OcSheetV1Events, sheetV1 } from "@otto-ec/otto-components/sheet";
import { SvelteFacet } from "../filter/SvelteFacet";
import { eventQBus } from "../types/EventQBus";
import {
  copyLegacyLabels,
  readLegacyLabel,
  setLegacyLabels,
  updateLabel,
  updateLegacyLabel,
} from "../tracking/FeatureTracking";
import { registerClickTracker, sanFoldout } from "./FilterSheetTracking";
import { createEventMergeId, submitEventTracking } from "../tracking/TrackingUtils";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { OcFilterAccordion } from "../filter/OcFilterAccordion";
import { FilterTitle } from "../filter/FilterTitle";
import { isActive } from "../toggle/Toggle";
import { Filter } from "../filter/Filter";

const FILTER_DETAIL_SHEET_ID = "heureka-filterdetailsheet";
const FILTER_DETAIL_SHEET_CONTENT_CLASS = "heureka_filterDetailSheet__content";
const FILTER_DETAIL_SHEET_ACTIONS_CLASS = "heureka_filterDetailSheet__actions";
const FILTER_DETAIL_SHEET_NBF_CLASS = "heureka_filterDetailSheet--nbf";
const FILTER_DETAIL_SHEET_CONTENT_SELECTOR = `.${FILTER_DETAIL_SHEET_CONTENT_CLASS}`;
const FILTER_DETAIL_SHEET_ACTIONS_SELECTOR = `.${FILTER_DETAIL_SHEET_ACTIONS_CLASS}`;

function removeFacetChangeNothing(elem: HTMLElement, event: Event, abortWatch: AbortController) {
  const target = event.target as HTMLInputElement;
  if (target.form && (hasChanges(target.form) || isForceSubmit(target.form))) {
    updateLegacyLabel(elem, "san_FacetChangeNothing");
    updateLegacyLabel(elem, "san_FacetSearchTerm");
    updateLegacyLabel(elem, "san_FacetSearchType");
    abortWatch.abort();
  }
}

export function addFormChangeWatcher(form: HTMLFormElement, elem: HTMLElement, resettable = true) {
  const facetChangeNothing = readLegacyLabel(elem, "san_FacetChangeNothing");
  copyLegacyLabels(form, elem);

  if (facetChangeNothing) {
    watchEvent(
      form,
      "input",
      (event, abortWatch) => {
        copyLegacyLabels(form, elem);
        removeFacetChangeNothing(elem, event, abortWatch);
      },
      resettable ? "submit" : undefined,
      () => {
        updateLegacyLabel(elem, "san_FacetChangeNothing", facetChangeNothing);
        copyLegacyLabels(form, elem);
      },
    );
  }
}

export default class OcFilterDetailSheet {
  /*               */

  constructor(
    private readonly filterId: string,
    readonly sheet: sheetV1 | undefined = getElementById(FILTER_DETAIL_SHEET_ID),
    private readonly accordion: OcFilterAccordion | undefined = filterId
      ? OcFilterAccordion.filterId(filterId)
      : undefined,
    private readonly filterTitle: FilterTitle | undefined = filterId ? FilterTitle.filterId(filterId) : undefined,
    private readonly sheetContent: HTMLElement | null | undefined = sheet?.querySelector(
      FILTER_DETAIL_SHEET_CONTENT_SELECTOR,
    ),
    private readonly sheetActions: HTMLElement | null | undefined = sheet?.querySelector(
      FILTER_DETAIL_SHEET_ACTIONS_SELECTOR,
    ),
  ) {
    if (this.sheet) {
      this.sheet.headline = accordion?.filter?.filterTitle?.filterTitle;
    }
  }

  public static createForFilter(filterId: string) {
    return new OcFilterDetailSheet(filterId);
  }

  /*                                                                                                   */
  public static isCurrentlyNbf(): boolean {
    return !!getElementById(FILTER_DETAIL_SHEET_ID)?.classList.contains(FILTER_DETAIL_SHEET_NBF_CLASS);
  }

  public static addListeners() {
    const sheet: sheetV1 | undefined = getElementById(FILTER_DETAIL_SHEET_ID);
    const sheetContent: HTMLElement | null | undefined = sheet?.querySelector(FILTER_DETAIL_SHEET_CONTENT_SELECTOR);

    sheet?.addEventListener("oc-sheet-before-open", () => {
      /*                                                      */
      if (!sheetContent || sheetContent.childElementCount > 0) {
        return;
      }

      const filterId = sheetContent.dataset.filterId;
      if (!filterId) {
        return;
      }

      const detailSheet = OcFilterDetailSheet.createForFilter(filterId);
      if (sheet?.classList.contains(FILTER_DETAIL_SHEET_NBF_CLASS)) {
        detailSheet.setNextBestFilterSheet().initializeNextBestFilterSheet();
      } else {
        detailSheet.setSheet().initializeSheet();
      }
    });
  }

  /*       */

  setSheet() {
    const form = filter(this.filterId);
    if (form) {
      if (this.sheetContent) {
        this.sheetContent.appendChild(form);
      }
      if (this.sheetActions) {
        moveAllChildren(filterFooter(form), this.sheetActions, true);
      }
      this.sheet?.classList.remove(FILTER_DETAIL_SHEET_NBF_CLASS);
    }
    return this;
  }

  setNextBestFilterSheet() {
    const form = filter(this.filterId);
    if (form) {
      if (this.sheetContent) {
        this.sheetContent.appendChild(form);
      }
      if (this.sheetActions) {
        moveAllChildren(nextBestActionButtons(form), this.sheetActions, true);
      }
      if (this.sheet) {
        this.sheet.classList.add(FILTER_DETAIL_SHEET_NBF_CLASS);
      }
    }
    return this;
  }

  initializeNextBestFilterSheet() {
    if (this.sheet) {
      this.sheet.removeAttribute("full-height");
      toggleFilterAccordion(this.filterId, true);
      setActiveConfiguration("sheetSecondLevel", this.filterId);
      setFacetLocation("nbf_sheet", this.filterId);
      this.sheet?.addEventListener("oc-sheet-after-open", this.afterOpen);
      this.sheet?.addEventListener("oc-sheet-before-close", this.beforeClose);

      this.open();

      this.refreshFacetChangeNothingLabel();

      const form = filter(this.filterId);
      if (this.sheetContent && form) {
        this.prepareContent(this.sheetContent, form, "close_nbf", "nbf");
        updateLabel(form, "san_FilterFoldoutStatus", "opened");
      }
    }
    return this;
  }

  initializeSheet() {
    if (this.sheet) {
      toggleFilterAccordion(this.filterId, true);
      setActiveConfiguration("sheetSecondLevel", this.filterId);
      setFacetLocation("second_level", this.filterId);
      this.sheet?.addEventListener("oc-sheet-after-open", this.afterOpen);
      this.sheet?.addEventListener("oc-sheet-before-close", this.beforeClose);

      this.open();

      this.refreshFacetChangeNothingLabel();

      const form = filter(this.filterId);
      if (this.sheetContent && form) {
        this.prepareContent(this.sheetContent, form);
        updateLabel(form, "san_FilterFoldoutStatus", "opened");
      }
    }
    return this;
  }

  /*                       */

  private deactivate() {
    this.sheet?.removeEventListener("oc-sheet-after-open", this.afterOpen);
    this.sheet?.removeEventListener("oc-sheet-before-close", this.beforeClose);
    const source = this.sheetContent;
    if (source) {
      const detailSheetFacet = SvelteFacet.factory.pick(undefined, source);
      detailSheetFacet?.updateFeatureStatus("visible", "loaded");
    }

    if (isActive("HEUREKA_2398_OPTIMIZE_FILTER_SHEET_PERFORMANCE")) {
      const { filterTitle } = this;
      const filter = Filter.filterId(this.filterId);
      if (filterTitle && filter) {
        filterTitle.filter = filter;
        if (this.sheet?.classList.contains(FILTER_DETAIL_SHEET_NBF_CLASS)) {
          /*                                                           */
          /*                                                      */
          filter.nextBestActionButtons = this.sheetActions;
          this.sheet?.setAttribute("full-height", "");
        } else {
          filter.filterFooter = this.sheetActions;
        }
        filter.filterFoldoutStatus = "closed";
      }
    } else {
      const { accordion } = this;
      if (accordion) {
        accordion.filterForm = filter(this.filterId);
        if (this.sheet?.classList.contains(FILTER_DETAIL_SHEET_NBF_CLASS)) {
          /*                                                           */
          /*                                                      */
          accordion.nextBestActionButtons = this.sheetActions;
          this.sheet?.setAttribute("full-height", "");
        } else {
          accordion.filterFooter = this.sheetActions;
        }
        accordion.filterFoldoutStatus = false;
      }
    }
    /*                                       */
    resetFacetLocation();

    return this;
  }

  open() {
    if (this.sheet) {
      this.sheet.open = true;
    }
    return this;
  }

  public back() {
    this.sheet?.back();
    return this;
  }

  /*       */

  private prepareContent(
    content: HTMLElement,
    form: HTMLFormElement,
    san_FilterFoldoutActivity: TrackingLabels["san_FilterFoldoutActivity"] = "close",
    san_FacetChangeNothing: TrackingLabels["san_FacetChangeNothing"] = "view_selection",
  ) {
    content.scrollTop = 0;
    content.dataset.filterId = this.filterId;

    setLegacyLabels(content, {
      san_FilterFoldoutType: this.filterId,
      san_FilterFoldoutActivity,
      san_FacetChangeNothing,
    });

    addFormChangeWatcher(form, content, false);
  }

  private afterOpen = () => {
    registerClickTracker(this.sheet, {
      capture: true,
      passive: true,
    });
    eventQBus.emit("ftfind.filterSheet.openedDetail", { filterId: this.filterId });
  };

  private beforeClose = (closeEvent: OcSheetV1Events["oc-sheet-close"]) => {
    const form = filter(this.filterId);
    if (form instanceof HTMLFormElement) {
      form.dataset.tsFeatureFilterMethod = this.filterMethodFrom(closeEvent.detail.closeType);
      fireFilterSubmit(form);
    }
    this.deactivate();
    /*                                 */
    if (
      closeEvent.detail.closeType === "closedWithBackNavigation" ||
      closeEvent.detail.closeType === "closedWithUserAgent"
    ) {
      const { tsLabels } = this.sheetContent.dataset;
      const parsedLabels = JSON.parse(tsLabels);
      if (readLegacyLabel(this.sheetContent, "san_FacetChangeNothing")) {
        parsedLabels.san_FacetChangeNothing = "back";
      }
      submitEventTracking(parsedLabels, createEventMergeId());
      eventQBus.emit("ftfind.filterSheet.closeDetail", { filterId: this.filterId });
      /*                                                */
      closeEvent.preventDefault();
      return;
    }
    /*                        */
    if (closeEvent.detail.closeType !== "closedProgrammatically") {
      const { filterId, tsLabels } = this.sheetContent.dataset;
      const san_FilterWhatIf = readLegacyLabel(filterSheetButton(), "san_FilterWhatIf");
      eventQBus.emit("ftfind.filterSheet.closed", {
        filterId,
        tracking: {
          san_FilterWhatIf,
          san_Foldout: sanFoldout(closeEvent.detail.closeType),
          ...JSON.parse(tsLabels),
        },
      });
    }
  };

  public emitFilterSheetDetailClosedEvent() {
    if (this.sheet) {
      eventQBus.emit("ftfind.filterSheet.closedDetail", { filterId: this.filterId });
    }
    return this;
  }

  private refreshFacetChangeNothingLabel() {
    forEachElement(
      ".find_filterSheet__menuBackButton[type=submit]",
      (elem) => updateLegacyLabel(elem, "san_FacetChangeNothing", "adopt_down"),
      this.sheetActions,
    );
  }

  private filterMethodFrom(closeType: string): string {
    switch (closeType) {
      case "closedWithBackNavigation":
        return "adopt";
      case "closedProgrammatically":
        return "view_selection";
      default:
        return closeType;
    }
  }
}
